/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { debounce, isEqual, throttle } from 'lodash';
import type { Extent } from 'ol/extent';
import { containsExtent, equals } from 'ol/extent';
import type ImageWrapper from 'ol/Image';
import ImageState from 'ol/ImageState';
import LayerProperty from 'ol/layer/Property';
import type { Projection } from 'ol/proj';
import { fromResolutionLike } from 'ol/resolution';
import ImageSource, { getRequestExtent } from 'ol/source/Image';
import type { WMImage } from '@opengeoweb/webmap';
import { WMImageStoreLoadType, WMInvalidDateValues } from '@opengeoweb/webmap';
import {
  NUMBER_PREFETCH_WORKERS,
  MAX_NUMBER_TO_PREFETCH,
  ENABLE_PREFETCH,
  FAILED_IMAGE_RETRY_CHECK_INTERVAL_MS,
  WMS_EXTENT_RATIO_TO_VIEWPORT,
} from './constants';
import TimeAwareImageWrapper, {
  openLayersGetMapImageStore,
} from './TimeAwareImageWrapper';
import { WorkerQueue } from './WorkerQueue';
import type { FinaliseFn, WorkerQueueJob } from '../types/WorkerQueue';

export const initImageWrapper = (): TimeAwareImageWrapper => {
  const emptyButLoading = new TimeAwareImageWrapper([0, 0, 0, 0], 1, 1);

  // Set to loaded to indicate to ol that it can be displayed immediately
  emptyButLoading.setState(ImageState.LOADED);
  emptyButLoading.setImage(document.createElement('img'));
  return emptyButLoading;
};

interface TimeawareImageSourceOptions {
  wmsUrl: string;
  params?: Record<string, string>;
  layerName: string;
  layerId: string;
  dimProps?: Record<string, string>;
  styleName?: string | undefined;
  timeStampsToPrefetch?: string[];
  visible?: boolean;
  // Ratio of the requested WMS GetMap extent to the viewport extent. Defaults to WMS_EXTENT_RATIO_TO_VIEWPORT (1.2);
  // pass 1 to request an extent that matches the viewport exactly.
  extentRatio?: number;
}

interface PrefetchState {
  timeStampsToPrefetch: string[];
  extent: Extent;
  layerName: string;
  styleName: string;
  dimProperties: Record<string, string>;
}

export class TimeawareImageSource extends ImageSource {
  private _wmsUrl: string;
  private _extraWMSParams: Record<string, string>;
  private _timeStampsToPrefetch: string[];
  private _prefetchWorkers: WorkerQueue;
  private _lastRequestedExtent!: Extent;
  private _lastRequestedResolution!: number;
  private _lastRequestedPixelRatio!: number;
  private _lastRequestedProjection!: Projection;
  private _loadedExtent!: Extent;
  private _loadedResolution!: number;
  private _loadedPixelRatio!: number;
  private _loadedProjection!: Projection;
  private _previousPrefetchExtent: PrefetchState | null;
  private _dimProperties: Record<string, string>;
  private _imWrapperEmptyButLoading: TimeAwareImageWrapper;
  private _imWrapperForOlSource: TimeAwareImageWrapper;
  private _imWrapperForAltImage: TimeAwareImageWrapper;
  private _styleName: string;
  private _layerName: string;

  private throttledPrefetch: () => void;

  private throttledImageLoad: () => void;
  private _layerId: string;
  private _failedImageRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private _extentRatio: number;

  constructor({
    wmsUrl,
    timeStampsToPrefetch,
    layerName,
    layerId,
    styleName,
    dimProps,
    params,
    visible,
    extentRatio,
  }: TimeawareImageSourceOptions) {
    super({});
    this._wmsUrl = wmsUrl;
    this._extraWMSParams = { ...params };
    this._layerName = layerName;
    this._dimProperties = { ...dimProps };
    this._styleName = styleName || '';
    this._timeStampsToPrefetch = [...(timeStampsToPrefetch || [])];
    this._previousPrefetchExtent = null;
    this._prefetchWorkers = new WorkerQueue(NUMBER_PREFETCH_WORKERS);
    this._extentRatio = extentRatio ?? WMS_EXTENT_RATIO_TO_VIEWPORT;
    this.prefetch = this.prefetch.bind(this);
    this.isImageLoadingForTimestep = this.isImageLoadingForTimestep.bind(this);
    this.hasImageLoadedForTimeValue =
      this.hasImageLoadedForTimeValue.bind(this);
    this.setImageForTimeValue = this.setImageForTimeValue.bind(this);
    this._conditionalPrefetch = this._conditionalPrefetch.bind(this);
    // These throttle settings ensure that prefetching starts after 200ms of inactivity, but at the same time
    // if there is a continuous stream of changes (like when moving the map), it will still trigger at least every 2 seconds.
    // This ensures that prefetching also happens when the user is continuously interacting with the map, but at a throttled rate.
    this.throttledPrefetch = throttle(
      throttle(this._conditionalPrefetch, 2000, {
        leading: true,
        trailing: true,
      }),
      200,
      {
        leading: true,
        trailing: false,
      },
    );

    this.throttledImageLoad = debounce(this._loadImagery, 100, {
      leading: false,
      trailing: true,
    });
    this._layerId = layerId;

    this.triggerPrefetch = this.triggerPrefetch.bind(this);
    this.cancelPrefetch = this.cancelPrefetch.bind(this);
    this._imWrapperEmptyButLoading = initImageWrapper();
    this._imWrapperForOlSource = initImageWrapper();
    this._imWrapperForAltImage = initImageWrapper();
    this._imWrapperForOlSource.setExtentRatio(this._extentRatio);
    this._imWrapperForAltImage.setExtentRatio(this._extentRatio);
    this.set(LayerProperty.VISIBLE, !!visible);
  }

  private _conditionalPrefetch(): void {
    if (!ENABLE_PREFETCH) {
      return;
    }
    // If the extent has changed => trigger prefetching
    const extent = this._lastRequestedExtent;
    const resolution = this._lastRequestedResolution;
    const pixelRatio = this._lastRequestedPixelRatio;
    const projection = this._lastRequestedProjection;

    if (
      !this._previousPrefetchExtent?.extent ||
      !equals(extent, this._previousPrefetchExtent.extent) ||
      this._previousPrefetchExtent.layerName !== this._layerName ||
      this._previousPrefetchExtent.styleName !== this._styleName ||
      this._previousPrefetchExtent.timeStampsToPrefetch !==
        this._timeStampsToPrefetch ||
      this._previousPrefetchExtent.dimProperties !== this._dimProperties
    ) {
      this._previousPrefetchExtent = {
        extent,
        layerName: this._layerName,
        styleName: this._styleName,
        timeStampsToPrefetch: this._timeStampsToPrefetch,
        dimProperties: this._dimProperties,
      };

      this.prefetch(extent, resolution, pixelRatio, projection);
    }
  }

  private _loadImagery(dimProperties?: Record<string, string>): void {
    // Tell the imagestore that we are about to do a GetMap request
    openLayersGetMapImageStore.loadDuration.setLoadingStatusForId(
      WMImageStoreLoadType.GETMAP,
      this._layerId,
      0,
      1,
      true,
    );
    // Load the newly requested image
    this._imWrapperForOlSource
      .loadAndResolve(
        this._lastRequestedExtent,
        this._lastRequestedResolution,
        this._lastRequestedPixelRatio,
        this._lastRequestedProjection!,
        this._wmsUrl,
        this._extraWMSParams,
        this._layerName,
        this._styleName,
        dimProperties ? { ...dimProperties } : { ...this._dimProperties },
      )
      .then((el) => {
        // Tell the imagestore that GetMap request was finished.
        openLayersGetMapImageStore.loadDuration.setLoadDurationForId(
          this._layerId,
          el.getLoadDuration(),
        );
      })
      .catch(() => {
        // TODO, enable error handling
        // console.error(e);
      })
      .finally(() => {
        // Tell the imagestore that GetMap request was finished.
        openLayersGetMapImageStore.loadDuration.setLoadingStatusForId(
          WMImageStoreLoadType.GETMAP,
          this._layerId,
          1,
          1,
        );
        this._loadedExtent = this._lastRequestedExtent;
        this._loadedResolution = this._lastRequestedResolution;
        this._loadedPixelRatio = this._lastRequestedPixelRatio;
        this._loadedProjection = this._lastRequestedProjection!;
        // Loading finished, tell openlayers that we need to change. It will then call getImage again, which will display this loaded image.
        this.changed();

        this.triggerPrefetch();
      });
  }

  private prefetch(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
  ): void {
    const dimensionsToPrefetchUnFiltered = this._timeStampsToPrefetch;

    // Only work on imagery that still needs to be loaded.
    const dimensionsToPrefetch = dimensionsToPrefetchUnFiltered.filter(
      (dimValue) => {
        const hasExactImage = this._imWrapperForOlSource.hasImage(
          extent,
          resolution,
          pixelRatio,
          projection,
          this._wmsUrl,
          this._extraWMSParams,
          this._layerName,
          this._styleName,
          { ...this._dimProperties, TIME: dimValue },
        );
        return hasExactImage === false || !hasExactImage.isLoadedWithoutErrors;
      },
    );
    if (dimensionsToPrefetch.length === 0) {
      return;
    }
    this._prefetchWorkers.clearJobs();

    const jobs = dimensionsToPrefetch
      .slice(0, MAX_NUMBER_TO_PREFETCH)
      .map((dimValue): WorkerQueueJob => {
        return {
          dimValue,
          cancelled: false,
          task: async (): Promise<FinaliseFn> => {
            const jobWrapper = new TimeAwareImageWrapper([0, 0, 0, 0], 1, 1);
            jobWrapper.setExtentRatio(this._extentRatio);
            const dimPropsWithTime = { ...this._dimProperties, TIME: dimValue };
            const requests = jobWrapper.getWMSGetMapRequestURLs(
              extent,
              resolution,
              pixelRatio,
              projection,
              this._wmsUrl,
              this._extraWMSParams,
              this._layerName,
              this._styleName,
              dimPropsWithTime,
            );
            requests.forEach(({ src }) => {
              openLayersGetMapImageStore.getImage(src)?.clearError();
            });
            await jobWrapper
              .loadAndResolve(
                extent,
                resolution,
                pixelRatio,
                projection,
                this._wmsUrl,
                this._extraWMSParams,
                this._layerName,
                this._styleName,
                dimPropsWithTime,
              )
              .catch((e) => {
                // Do not log errors, as they are not very useful and can better be inspected via the network console
              });
            return ((cancelled: boolean) => {
              openLayersGetMapImageStore.loadDuration.setLoadingStatusForId(
                WMImageStoreLoadType.PREFETCH,
                this._layerId,
                dimensionsToPrefetch.length -
                  this._prefetchWorkers.taskQueue.length,
                dimensionsToPrefetch.length,
              );
            }) as FinaliseFn;
          },
        };
      });
    this._prefetchWorkers.addJobs(jobs);
  }

  triggerPrefetch(): void {
    if (this._timeStampsToPrefetch.length > 0) {
      this.throttledPrefetch();
    }
  }

  cancelPrefetch(): void {
    this._prefetchWorkers.clearJobs();
  }

  getImage(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
  ): ImageWrapper {
    const requestExtent = getRequestExtent(extent, resolution, pixelRatio, 1);
    const requestResolution = this.findNearestResolution(resolution);
    const requestDimProperties = { ...this._dimProperties };

    const requestedImageHasWantedExtent =
      ((this._loadedExtent &&
        containsExtent(this._loadedExtent, requestExtent)) ||
        containsExtent(
          this._imWrapperForOlSource.getExtent(),
          requestExtent,
        )) &&
      ((this._loadedResolution &&
        fromResolutionLike(this._loadedResolution) === requestResolution) ||
        fromResolutionLike(this._imWrapperForOlSource.getResolution()) ===
          requestResolution);

    const hasExactImage =
      this._loadedExtent &&
      this._imWrapperForOlSource.hasImage(
        this._loadedExtent,
        this._loadedResolution,
        pixelRatio,
        projection,
        this._wmsUrl,
        this._extraWMSParams,
        this._layerName,
        this._styleName,
        requestDimProperties,
      );

    if (
      hasExactImage &&
      hasExactImage.isLoaded &&
      requestedImageHasWantedExtent
    ) {
      if (!hasExactImage.hasError) {
        this._imWrapperForOlSource.setGeoReferencedImage(hasExactImage);
        return this._imWrapperForOlSource;
      }
      // The image failed to load. Do not display the broken image, but check again later: the image is reloaded once it becomes stale.
      this._scheduleFailedImageRetry();
      return this._getAltImageOrEmpty(
        requestExtent,
        requestResolution,
        pixelRatio,
        projection,
        requestDimProperties,
      );
    }

    this._lastRequestedExtent = requestExtent;
    this._lastRequestedResolution = requestResolution;
    this._lastRequestedPixelRatio = pixelRatio;
    this._lastRequestedProjection = projection;

    this.throttledImageLoad();

    // While we are loading, check if there is an alternative image available
    return this._getAltImageOrEmpty(
      this._lastRequestedExtent,
      this._lastRequestedResolution,
      this._lastRequestedPixelRatio,
      this._lastRequestedProjection,
      requestDimProperties,
    );
  }

  private _getAltImageOrEmpty(
    extent: Extent,
    resolution: number,
    pixelRatio: number,
    projection: Projection,
    dimProperties: Record<string, string>,
  ): TimeAwareImageWrapper {
    const altImage =
      this._loadedExtent &&
      this._imWrapperForOlSource.getAlternativeImage(
        extent,
        resolution,
        pixelRatio,
        projection,
        this._wmsUrl,
        this._extraWMSParams,
        this._layerName,
        this._styleName,
        dimProperties,
      );

    if (altImage && altImage.isLoaded) {
      // A similar image is available for display, show it
      this._imWrapperForAltImage.setGeoReferencedImage(altImage);
      return this._imWrapperForAltImage;
    }

    // This thing is has nothing to show yet.
    return this._imWrapperEmptyButLoading;
  }

  private _scheduleFailedImageRetry(): void {
    if (this._failedImageRetryTimer) {
      return;
    }
    this._failedImageRetryTimer = setTimeout(() => {
      this._failedImageRetryTimer = null;
      this.changed();
    }, FAILED_IMAGE_RETRY_CHECK_INTERVAL_MS);
  }

  disposeInternal(): void {
    if (this._failedImageRetryTimer) {
      clearTimeout(this._failedImageRetryTimer);
      this._failedImageRetryTimer = null;
    }
    this.cancelPrefetch();
    super.disposeInternal();
  }

  setDimProps(params: Record<string, string>): void {
    if (isEqual(this._dimProperties, params)) {
      return;
    }
    this._dimProperties = { ...params };
    this.changed();
  }

  /**
   * Check if an image is available and loaded for requested date. Used for showlayerinfo to proceed.
   * @param isoTime
   * @returns
   */
  hasImageLoadedForTimeValue(isoTime: string): boolean {
    if (!isoTime || WMInvalidDateValues.has(isoTime)) {
      return false;
    }
    if (this._loadedExtent?.length !== 4) {
      return false;
    }
    const dimPropWithRequestedDate = { ...this._dimProperties };
    dimPropWithRequestedDate['TIME'] = isoTime;

    const image = this._imWrapperForOlSource.hasImage(
      this._lastRequestedExtent,
      this._lastRequestedResolution,
      this._lastRequestedPixelRatio,
      this._lastRequestedProjection,
      this._wmsUrl,
      this._extraWMSParams,
      this._layerName,
      this._styleName,
      dimPropWithRequestedDate,
    );
    if (image === false || !image?.isLoaded) {
      return false;
    }
    return true;
  }

  getAltGeoReferencedImages(isoTime: string): WMImage[] {
    if (!isoTime || WMInvalidDateValues.has(isoTime)) {
      return [];
    }
    if (this._loadedExtent?.length !== 4) {
      return [];
    }
    const dimPropWithRequestedDate = { ...this._dimProperties };
    dimPropWithRequestedDate['TIME'] = isoTime;

    const requests = this._imWrapperForOlSource.getWMSGetMapRequestURLs(
      this._lastRequestedExtent,
      this._lastRequestedResolution,
      this._lastRequestedPixelRatio,
      this._lastRequestedProjection,
      this._wmsUrl,
      this._extraWMSParams,
      this._layerName,
      this._styleName,
      dimPropWithRequestedDate,
    );

    return requests.flatMap(({ src, extent, resolution }) =>
      openLayersGetMapImageStore.getAltGeoReferencedImages(
        src,
        extent,
        resolution,
      ),
    );
  }
  /**
   * This function can be used to determine if the animation can proceed to the next step.
   * @param isoTime ISO8601 string formatat like `YYYY-MM-DDTHH:MM:SSZ`
   * @returns Only specic case it returns false: when a image is available and is loading. That means we wait till it is loaded.
   *          This function returns true if an image is not available in the store. Or true if it is available and already loaded.
   */
  isImageLoadingForTimestep(isoTime: string): boolean {
    const geoRefs = this.getAltGeoReferencedImages(isoTime);

    if (geoRefs.length === 0) {
      // If there is no image at all for requested timestep, it means that the layer does not advertise data for that timestep.
      // In that case the animation should proceed, so return true here to avoid blocking the animation.
      return true;
    }
    // If it is still loading, we can block the animation going forward.
    return geoRefs.filter((geoRef) => !geoRef.isLoading()).length !== 0;
  }

  setImageForTimeValue(timeStampsToPrefetch: string): void {
    if (this._loadedExtent?.length !== 4) {
      return;
    }
    const dimPropWithRequestedDate = { ...this._dimProperties };
    dimPropWithRequestedDate['TIME'] = timeStampsToPrefetch;

    // While we are loading, check if there is an alternative image available
    const altImage =
      this._loadedExtent &&
      this._imWrapperForOlSource.getAlternativeImage(
        this._loadedExtent,
        this._loadedResolution,
        this._loadedPixelRatio,
        this._loadedProjection,
        this._wmsUrl,
        this._extraWMSParams,
        this._layerName,
        this._styleName,
        dimPropWithRequestedDate,
      );

    if (altImage && altImage.isLoaded) {
      // A similar image is available for display, show it
      this._imWrapperForAltImage.setGeoReferencedImage(altImage);
    }
  }

  setStyle(styleName: string): void {
    if (this._styleName === styleName) {
      return;
    }
    this._styleName = styleName;
    this.changed();
  }

  setLayerName(layerName: string): void {
    if (this._layerName === layerName) {
      return;
    }
    this._layerName = layerName;
    this.changed();
  }

  /**
   * Sets the layer name, style and dimensions at once, so the source never requests a layer with the style or dimensions of another layer.
   */
  setLayerProps({
    layerName,
    styleName,
    dimProps,
  }: {
    layerName: string;
    styleName: string;
    dimProps: Record<string, string>;
  }): void {
    const dimPropsChanged = !isEqual(this._dimProperties, dimProps);
    if (
      this._layerName === layerName &&
      this._styleName === styleName &&
      !dimPropsChanged
    ) {
      return;
    }
    this._layerName = layerName;
    this._styleName = styleName;
    // Only replace when changed, prefetching compares dimProperties by reference
    if (dimPropsChanged) {
      this._dimProperties = { ...dimProps };
    }
    this.changed();
  }

  setTimeValuesValuesToPrefetch(timeStampsToPrefetch: string[]): void {
    this._timeStampsToPrefetch = timeStampsToPrefetch;
    this.triggerPrefetch();
  }
}
